import React, { useState } from "react";
import { LucideIcon, Trash2 } from "lucide-react";
import DeleteDocumentDialog from "@/components/Documents/DeleteDocumentDialog";
import type { DeletableDocumentType } from "@/lib/documents/deletion";
import { Input } from "@/components/ui/input";
import ShareExternallyDialog, { ShareTarget } from "@/components/Sharing/ShareExternallyDialog";
import DocumentExplanation from "@/components/BasProcess/DocumentExplanation";
import ExportHistoryDialog, { ExportHistoryTarget } from "@/components/Documents/ExportHistoryDialog";
import AuditLogDialog from "@/components/Documents/AuditLogDialog";
import VersionHistoryDialog from "@/components/Documents/VersionHistoryDialog";
import DocumentSyncStatus from "@/components/Documents/DocumentSyncStatus";
import type { VersionTarget } from "@/lib/documents/versions";
import type { AuditTarget } from "@/lib/audit/documentAudit";
import { getExplanation } from "@/lib/basProcess/explanations";
import { Button } from "@/components/ui/button";
import useSimpleMode from "@/hooks/useSimpleMode";

interface DocumentHeaderProps {
  /** Dokumentnummer enligt Bas-processen, t.ex. "20" eller "02 / 31" */
  docNumber?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Lagreferens som visas i chipraden, t.ex. "AFS 2023:3 kap. 4" */
  legalRef?: string;
  icon: LucideIcon;
  /** Knappar till höger */
  actions?: React.ReactNode;
  /** Badges, progress eller annan status – visas ovanför/bredvid knapparna */
  meta?: React.ReactNode;
  /** Gör titeln redigerbar (används av arbetsmiljöplanen) */
  editableTitle?: { value: string; onChange: (value: string) => void };
  /** Aktiverar "Dela externt"-knappen för dokumentet */
  share?: ShareTarget;
  /** Visar versionshistorik för Word-exporter av dokumentet */
  exportHistory?: ExportHistoryTarget;
  /** Visar ändringslogg: vem som ändrat vad och när */
  audit?: AuditTarget;
  /** Visar versionshistorik för dokumentets innehåll (med återställning) */
  versions?: { type: VersionTarget["type"]; id: string; name?: string; onRestored?: () => void | Promise<void> };
  /** Visar synkstatus: aktiva delningslänkar och SharePoint/OneDrive-kopior */
  syncStatus?: { documentId: string };
  /** Aktiverar knappen "Ta bort dokument" (flyttar till papperskorgen) */
  deletion?: {
    type: DeletableDocumentType;
    id: string;
    name: string;
    onDeleted?: () => void | Promise<void>;
  };

  /** Projektet dokumentet tillhör – används för att visa "ej aktuellt" */
  projectId?: string;
  /** Extra innehåll längst ned i panelen (t.ex. informationsrad) */
  footer?: React.ReactNode;
  /** Kompakt läge: mindre padding och ingen "Varför behövs det här?"-ruta */
  compact?: boolean;
  className?: string;
}

/**
 * Gemensam rubrikpanel för alla dokument i appen så att
 * samtliga dokumentvyer får samma premiumkänsla.
 */
export const DocumentHeader: React.FC<DocumentHeaderProps> = ({
  docNumber,
  title,
  description,
  legalRef,
  icon: Icon,
  actions,
  meta,
  editableTitle,
  share,
  exportHistory,
  audit,
  versions,
  syncStatus,
  deletion,
  projectId,
  footer,
  compact = false,
  className = "",
}) => {
  const purpose = compact ? undefined : getExplanation(docNumber);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const simpleMode = useSimpleMode(projectId);
  const docKeys = (docNumber ?? "").split("/").map((d) => d.trim()).filter(Boolean);
  const inactiveKey = docKeys.find((d) => d in simpleMode.reasons);
  const inactiveReason = inactiveKey ? simpleMode.reasons[inactiveKey] : undefined;
  return (
  <div
    className={`relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-muted/70 via-background to-background shadow-sm ${
      compact ? "px-4 py-3.5 sm:px-5 sm:py-4" : "p-5 sm:p-6"
    } ${className}`}
  >
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-primary/5 blur-3xl"
    />
    {inactiveKey && (
      <div className="relative mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/40 px-3 py-2">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Ej aktuellt i det här projektet.</span>{" "}
          {inactiveReason ?? "Markerat som ej aktuellt."}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void simpleMode.restore(inactiveKey)}
        >
          Gör aktuellt igen
        </Button>
      </div>
    )}
    <div className={`relative flex flex-wrap items-start justify-between ${compact ? "gap-3" : "gap-5"}`}>
      <div className={`flex min-w-0 flex-1 items-start ${compact ? "gap-3" : "gap-4"}`}>
        <div
          className={`flex shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/15 ${
            compact ? "h-9 w-9" : "h-11 w-11"
          }`}
        >
          <Icon className={compact ? "h-4 w-4" : "h-5 w-5"} />
        </div>
        <div className={`min-w-0 ${compact ? "space-y-1" : "space-y-1.5"}`}>
          {(docNumber || legalRef) && (
            <div className="flex flex-wrap items-center gap-2">
              {docNumber && (
                <span className="inline-flex items-center text-[11px] font-normal text-muted-foreground">
                  Dok {docNumber}
                </span>
              )}
              {legalRef && (
                <span className="inline-flex items-center rounded-md border border-border/70 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {legalRef}
                </span>
              )}
            </div>
          )}

          {editableTitle ? (
            <Input
              className="h-11 border-transparent bg-transparent px-0 text-lg font-semibold shadow-none focus-visible:border-input focus-visible:bg-background focus-visible:px-3"
              defaultValue={editableTitle.value}
              onBlur={(e) => editableTitle.onChange(e.target.value)}
            />
          ) : (
            <h1
              className={`font-semibold leading-tight tracking-tight ${
                compact ? "text-base" : "text-lg sm:text-xl"
              }`}
            >
              {title}
            </h1>
          )}

          {description && (
            <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">{description}</p>
          )}

          {purpose && (
            <p className="max-w-2xl rounded-md bg-primary/5 px-3 py-2 text-sm leading-relaxed text-foreground/80">
              <span className="font-medium text-primary">Varför behövs det här? </span>
              {purpose.why}
              <span className="text-muted-foreground"> Ansvarig: {purpose.owner}.</span>
            </p>
          )}
        </div>
      </div>

      {(meta || actions || share || exportHistory || audit || versions || deletion || docNumber) && (
        <div className="flex flex-wrap items-center gap-3">
          {meta}
          <DocumentExplanation docNumber={docNumber} />
          {exportHistory && <ExportHistoryDialog target={exportHistory} />}
          {audit && <AuditLogDialog target={audit} />}
          {versions && (
            <VersionHistoryDialog
              target={{ ...versions, docNumber, projectId }}
              onRestored={versions.onRestored}
            />
          )}
          {share && <ShareExternallyDialog target={share} />}
          {actions}
          {deletion && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Ta bort dokument
              </Button>
              <DeleteDocumentDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                target={{
                  type: deletion.type,
                  id: deletion.id,
                  name: deletion.name,
                  docNumber,
                  projectId,
                }}
                onDeleted={deletion.onDeleted}
              />
            </>
          )}
        </div>
      )}
    </div>

    {syncStatus && (
      <DocumentSyncStatus
        className="relative mt-4"
        target={{ projectId, documentId: syncStatus.documentId, docNumber }}
      />
    )}

    {footer && <div className="relative mt-4 border-t border-border/60 pt-3 text-sm text-muted-foreground">{footer}</div>}
  </div>
  );
};

export default DocumentHeader;
